/**
 * Co-curricular Presets data hooks (Phase 2 - Group D admin surface).
 *
 * Thin TanStack-Query wrappers around the pymble-apis Preset endpoints. Two
 * read families exist:
 *
 *   - usePresetsList()      -> GET /group/admin/presets/   (site-admin only)
 *   - useSharedPresetsList()-> GET /group/presets/?audience=shared (any caller
 *                              who can read them - manager / owner / admin)
 *
 * Mutations cover the full lifecycle: create-on-behalf (admin), update,
 * delete (soft), materialise, flip-audience. Cache invalidation matches the
 * portal's `invalidatePresetSurfaces` pattern so manager listings, detail,
 * and the underlying `/groups/{id}` views all refresh after a write.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";
import type {
  AdminPresetListParams,
  Preset,
  PresetAdminCreateRequest,
  PresetCompanionAddRequest,
  PresetCreateRequest,
  PresetFlipAudienceRequest,
  PresetMembershipParams,
  PresetMembershipResponse,
  PresetUpdateRequest,
} from "../types/preset";

const PRESETS_USER_ENDPOINT = "/group/presets/";
const PRESETS_ADMIN_ENDPOINT = "/group/admin/presets/";

/** Shared cache namespace - every preset-related query key starts with this
 *  so we can wipe the whole admin tab with a single invalidate. */
export const PRESETS_ROOT_KEY = ["presets-admin"] as const;
export const PRESETS_LIST_KEY = [...PRESETS_ROOT_KEY, "list"] as const;
export const PRESETS_DETAIL_KEY = [...PRESETS_ROOT_KEY, "detail"] as const;
export const PRESETS_MEMBERSHIP_KEY = [
  ...PRESETS_ROOT_KEY,
  "membership",
] as const;

/**
 * Wipe every cache slice that derives from a preset (list view, detail
 * pane, AND the materialised Group surfaces `/groups/{id}` shares with the
 * portal). Mirrors my-pymble-portal/src/hooks/usePresetMutations.ts so the
 * two surfaces stay coherent.
 */
function invalidatePresetSurfaces(
  qc: QueryClient,
  presetId?: string | null
): void {
  qc.invalidateQueries({ queryKey: PRESETS_ROOT_KEY });
  if (!presetId) return;
  qc.invalidateQueries({ queryKey: [...PRESETS_DETAIL_KEY, presetId] });
  // `useGroupDetail` keys for the same id render preset.group_id metadata +
  // members + notices + events. Stale entries would show the wrong member
  // count badge or miss just-added contacts.
  qc.invalidateQueries({ queryKey: ["group-detail", presetId] });
  qc.invalidateQueries({ queryKey: ["group-detail-members", presetId] });
  qc.invalidateQueries({ queryKey: ["group-detail-notices", presetId] });
  qc.invalidateQueries({ queryKey: ["group-detail-events", presetId] });
}

/**
 * Invalidate BOTH halves of a companion pair.
 *
 * Every companion write changes two presets at once - the source gains or
 * loses `companion_preset_id`, the companion is created or soft-deleted - so
 * invalidating only the preset whose page you are standing on leaves the other
 * one cached. The visible bug is a source panel still offering "Add companion
 * parent group" immediately after one was created.
 *
 * Passing the same id twice, or a null second id, is a no-op beyond the first
 * pass: `invalidatePresetSurfaces` is idempotent.
 */
function invalidateCompanionPair(
  qc: QueryClient,
  sourceId?: string | null,
  companionId?: string | null
): void {
  invalidatePresetSurfaces(qc, sourceId);
  if (companionId && companionId !== sourceId) {
    invalidatePresetSurfaces(qc, companionId);
  }
}

function buildAdminListQuery(params: AdminPresetListParams): string {
  const search = new URLSearchParams();
  if (params.audience) search.set("audience", params.audience);
  if (params.deleted_at) search.set("deleted_at", params.deleted_at);
  if (params.owner_contact_id !== undefined) {
    search.set("owner_contact_id", String(params.owner_contact_id));
  }
  if (params.search) search.set("search", params.search);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.skip !== undefined) search.set("skip", String(params.skip));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Site-admin "show all" list. Backed by GET /group/admin/presets/. Returns
 * personal + shared by default (or filter via params.audience). NOT usable
 * by non-admin callers - the API returns 403, so the caller MUST gate the
 * `enabled` parameter on hasAdminAccess to avoid a guaranteed-failing
 * request on every non-admin page load (Codex review round 1 Med).
 */
export function usePresetsList(
  params: AdminPresetListParams,
  enabled = true,
) {
  const { callApi, isAuthenticated } = usePymbleApi();
  return useQuery<Preset[]>({
    queryKey: [...PRESETS_LIST_KEY, "admin", params],
    queryFn: async () => {
      const qs = buildAdminListQuery(params);
      const res = await callApi("GET", `${PRESETS_ADMIN_ENDPOINT}${qs}`);
      return Array.isArray(res) ? (res as Preset[]) : [];
    },
    enabled: isAuthenticated && enabled,
    staleTime: 1000 * 30,
  });
}

/**
 * Default Presets-tab list for a non-admin caller (or admin with the toggle
 * OFF). Filters to audience=shared so personal presets owned by other users
 * never leak. Backed by GET /group/presets/?audience=shared.
 *
 * Gated on `enabled` so admins (whose toggle defaults to the admin list)
 * don't waste a parallel /group/presets/ request on every mount.
 */
export function useSharedPresetsList(enabled = true) {
  const { callApi, isAuthenticated } = usePymbleApi();
  return useQuery<Preset[]>({
    queryKey: [...PRESETS_LIST_KEY, "shared"],
    queryFn: async () => {
      const res = await callApi(
        "GET",
        `${PRESETS_USER_ENDPOINT}?audience=shared`
      );
      return Array.isArray(res) ? (res as Preset[]) : [];
    },
    enabled: isAuthenticated && enabled,
    staleTime: 1000 * 30,
  });
}

/**
 * Single preset read. Site-admin / owner / manager all permitted. The hook
 * leaves the `enabled` gate to the consumer (route-driven id may be empty).
 */
export function usePresetDetail(presetId: string | undefined) {
  const { callApi, isAuthenticated } = usePymbleApi();
  return useQuery<Preset | null>({
    queryKey: [...PRESETS_DETAIL_KEY, presetId ?? ""],
    queryFn: async () => {
      if (!presetId) return null;
      const res = await callApi("GET", `${PRESETS_USER_ENDPOINT}${presetId}`);
      return (res ?? null) as Preset | null;
    },
    enabled: isAuthenticated && !!presetId,
    staleTime: 1000 * 30,
  });
}

/**
 * Site-admin membership read - GET /group/admin/presets/{id}/membership
 * (apis#1166). Returns the CURRENT members and a live count even when the
 * preset's group hides its member list, which is the state every companion
 * parent group sits in.
 *
 * This is a READ. It changes nothing: no visibility flip, no
 * `ShowMemberList`, no `VisibilityRevision` bump - which is the entire reason
 * it exists, since the only previous way to see a hidden roster was the
 * persisted reveal write. So there is deliberately no invalidation here, and
 * nothing else in this module invalidates on its behalf beyond the existing
 * root-key wipe (which correctly covers it after a materialise).
 *
 * Two deliberate query settings, both about the PII this returns:
 *
 *   - `enabled` must be driven by the CALLER, gated on both
 *     `hasAdminAccess` (the API answers 403 otherwise) and on the preview
 *     surface actually being open. Every call returns member identities and
 *     writes a server-side audit line, so it must be an act the operator
 *     asked for, never a background fetch on page load.
 *   - `gcTime: 0` drops the rows from the query cache as soon as the last
 *     consumer unmounts, rather than leaving a roster of families sitting in
 *     memory behind a closed dialog for the default five minutes.
 */
export function usePresetMembership(
  presetId: string | undefined,
  params: PresetMembershipParams = {},
  enabled = true
) {
  const { callApi, isAuthenticated } = usePymbleApi();
  const { limit, skip } = params;
  return useQuery<PresetMembershipResponse | null>({
    queryKey: [...PRESETS_MEMBERSHIP_KEY, presetId ?? "", { limit, skip }],
    queryFn: async () => {
      if (!presetId) return null;
      const search = new URLSearchParams();
      if (limit !== undefined) search.set("limit", String(limit));
      if (skip !== undefined) search.set("skip", String(skip));
      const qs = search.toString();
      const res = await callApi(
        "GET",
        `${PRESETS_ADMIN_ENDPOINT}${presetId}/membership${qs ? `?${qs}` : ""}`
      );
      return (res ?? null) as PresetMembershipResponse | null;
    },
    enabled: isAuthenticated && !!presetId && enabled,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Site-admin create-on-behalf. Always hits POST /group/admin/presets/ - the
 * regular POST /group/presets/ creates against the caller's identity, which
 * is wrong for admin tooling. Server forces `audience="shared"` and skips
 * owner-side visibility on constituents + managers.
 *
 * A body carrying `create_companion_parent_group` produces TWO presets from
 * this ONE request (the server links them atomically); the response is the
 * source, carrying `companion_preset_id`. Do not follow up with a second
 * create call.
 */
export function useCreatePresetAdmin() {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, PresetAdminCreateRequest>({
    mutationFn: async (body) => {
      // Force audience=shared client-side too so any UI hold-over from a
      // personal-style template can't accidentally send "personal" here.
      const payload = { ...body, audience: "shared" as const };
      const res = await callApi("POST", PRESETS_ADMIN_ENDPOINT, {
        data: payload,
      });
      return res as Preset;
    },
    onSuccess: (preset) => {
      invalidateCompanionPair(qc, preset?.id, preset?.companion_preset_id);
    },
  });
}

/**
 * Site-admin "add a companion parent group to an existing preset".
 * POST /group/admin/presets/{preset_id}/companion.
 *
 * Returns the newly created COMPANION preset, so the source id has to come
 * from the hook binding rather than the response. Server-side 422s the caller
 * should expect: `preset_already_has_companion` and
 * `companion_cannot_have_companion` (derivation does not chain).
 */
export function useAddPresetCompanion(presetId: string | undefined) {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, PresetCompanionAddRequest>({
    mutationFn: async (body) => {
      if (!presetId) throw new Error("presetId is required");
      const res = await callApi(
        "POST",
        `${PRESETS_ADMIN_ENDPOINT}${presetId}/companion`,
        { data: body }
      );
      return res as Preset;
    },
    onSuccess: (companion) => {
      invalidateCompanionPair(qc, presetId, companion?.id);
    },
  });
}

/**
 * Site-admin unlink. DELETE /group/admin/presets/{preset_id}/companion
 * SOFT-DELETES the companion and clears `companion_preset_id` on the source -
 * it is not a "detach and keep" operation, and the confirmation copy must say
 * so.
 *
 * Takes the companion's id as the mutation variable purely so both halves of
 * the pair can be invalidated; the request itself is addressed by source id.
 */
export function useUnlinkPresetCompanion(presetId: string | undefined) {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<void, Error, string | undefined>({
    mutationFn: async () => {
      if (!presetId) throw new Error("presetId is required");
      await callApi(
        "DELETE",
        `${PRESETS_ADMIN_ENDPOINT}${presetId}/companion`
      );
    },
    onSuccess: (_void, companionId) => {
      invalidateCompanionPair(qc, presetId, companionId);
    },
  });
}

/**
 * Owner-style create. Available for parity with the portal flow but the
 * admin tab generally uses {@link useCreatePresetAdmin}.
 */
export function useCreatePreset() {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, PresetCreateRequest>({
    mutationFn: async (body) => {
      const res = await callApi("POST", PRESETS_USER_ENDPOINT, { data: body });
      return res as Preset;
    },
    onSuccess: (preset) => {
      invalidatePresetSurfaces(qc, preset?.id);
    },
  });
}

export function useUpdatePreset(presetId: string | undefined) {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, PresetUpdateRequest>({
    mutationFn: async (body) => {
      if (!presetId) throw new Error("presetId is required");
      const res = await callApi("PUT", `${PRESETS_USER_ENDPOINT}${presetId}`, {
        data: body,
      });
      return res as Preset;
    },
    onSuccess: () => {
      invalidatePresetSurfaces(qc, presetId);
    },
  });
}

/**
 * Soft-delete (owner / site-admin only - V7 blocks managers with 403).
 * Pass the preset id at call time so the same hook handles list-row deletes
 * without re-binding per row.
 */
export function useDeletePreset() {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await callApi("DELETE", `${PRESETS_USER_ENDPOINT}${id}`);
    },
    onSuccess: (_void, id) => {
      invalidatePresetSurfaces(qc, id);
    },
  });
}

/**
 * Manual re-materialisation. Permitted for owner / manager / site-admin.
 * Useful when constituents have changed since the last hourly Dagster sweep
 * and the admin wants to force a refresh now.
 *
 * Materialising a SOURCE re-derives its companion in the same pass, so the
 * companion's surfaces are stale too. The response is the updated source and
 * carries `companion_preset_id`, which is why the pair is invalidated from it
 * rather than from a caller-supplied id - no call site has to remember. The
 * root-key invalidate already covers the companion's preset detail query; the
 * per-id pass is what reaches its `group-detail*` keys.
 */
export function useMaterialisePreset(presetId: string | undefined) {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, void>({
    mutationFn: async () => {
      if (!presetId) throw new Error("presetId is required");
      const res = await callApi(
        "POST",
        `${PRESETS_USER_ENDPOINT}${presetId}/materialise`
      );
      return res as Preset;
    },
    onSuccess: (preset) => {
      invalidateCompanionPair(qc, presetId, preset?.companion_preset_id);
    },
  });
}

/**
 * Site-admin only audience flip. Caller MUST clear `manager_contact_ids`
 * before flipping shared -> personal (V5 returns 422 with code
 * `manager_contact_ids_must_be_empty` otherwise).
 */
export function useFlipPresetAudience(presetId: string | undefined) {
  const { callApi } = usePymbleApi();
  const qc = useQueryClient();
  return useMutation<Preset, Error, PresetFlipAudienceRequest>({
    mutationFn: async (body) => {
      if (!presetId) throw new Error("presetId is required");
      const res = await callApi(
        "POST",
        `${PRESETS_USER_ENDPOINT}${presetId}/flip-audience`,
        { data: body }
      );
      return res as Preset;
    },
    onSuccess: () => {
      invalidatePresetSurfaces(qc, presetId);
    },
  });
}
